import { useCallback, useMemo, useRef, useState } from "react";

import {
  Container,
  Label,
  LabelText,
  RequiredMark,
} from "app/components/flat/ui/Inputs/InputText/InputText.styles";
import useClickOutside from "app/hooks/useClickOutside";

import type { FocusEvent, KeyboardEvent, ReactNode } from "react";

import * as S from "./SelectField.styles";

const DropdownIcon = () => (
  <svg
    width="10"
    height="5"
    viewBox="0 0 10 5"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ display: "block" }}
  >
    <path d="M0 0L5 5L10 0H0Z" fill="#5E5E60" />
  </svg>
);

type SelectFieldProps<T> = {
  label: ReactNode;
  required?: boolean;
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, value: T) => boolean;
  disabled?: boolean;
  readOnly?: boolean;
  hideLabel?: boolean;
  "data-testid"?: string;
};

function getOptionKey<T>(
  option: T,
  getOptionLabel: (option: T) => string,
): string {
  if (typeof option === "object" && option !== null) {
    const record = option as Record<string, unknown>;

    if (record.id !== undefined) {
      return String(record.id);
    }

    if (record.state !== undefined) {
      return String(record.state);
    }
  }

  return getOptionLabel(option);
}

let selectFieldIdCounter = 0;

function useGeneratedSelectId() {
  const idRef = useRef<string>();
  if (!idRef.current) {
    selectFieldIdCounter += 1;
    idRef.current = `select-field-${selectFieldIdCounter}`;
  }
  return idRef.current;
}

const SelectField = <T,>({
  label,
  required = false,
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  disabled = false,
  readOnly = false,
  hideLabel = false,
  "data-testid": dataTestId,
}: SelectFieldProps<T>) => {
  const fieldId = useGeneratedSelectId();
  const listboxId = `${fieldId}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const canOpen = !disabled && !readOnly;
  const isExpanded = isOpen && canOpen;

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const containerRef = useClickOutside(closeDropdown);

  const displayValue = value !== null ? getOptionLabel(value) : "";

  const optionItems = useMemo(
    () =>
      options.map((option) => ({
        option,
        key: getOptionKey(option, getOptionLabel),
        label: getOptionLabel(option),
        selected: value !== null ? isOptionEqualToValue(option, value) : false,
      })),
    [getOptionLabel, isOptionEqualToValue, options, value],
  );

  const openDropdown = useCallback(
    (preferredIndex?: number) => {
      if (!canOpen) {
        return;
      }

      if (optionItems.length > 0) {
        const selectedIndex = optionItems.findIndex((item) => item.selected);
        setActiveIndex(preferredIndex ?? (selectedIndex >= 0 ? selectedIndex : 0));
      } else {
        setActiveIndex(-1);
      }

      setIsOpen(true);
    },
    [canOpen, optionItems],
  );

  const handleToggle = () => {
    if (!canOpen) {
      return;
    }

    if (isOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  const handleSelect = (option: T) => {
    if (readOnly) {
      return;
    }

    onChange(option);
    closeDropdown();
  };

  const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocused = event.relatedTarget as Node | null;

    if (nextFocused && event.currentTarget.contains(nextFocused)) {
      return;
    }

    closeDropdown();
  };

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!canOpen) {
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          openDropdown();
        } else {
          setActiveIndex((current) => Math.min(current + 1, optionItems.length - 1));
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!isOpen) {
          openDropdown();
        } else if (optionItems.length > 0) {
          setActiveIndex((current) => Math.max(current - 1, 0));
        }
        break;
      case "Home":
        if (isOpen) {
          event.preventDefault();
          if (optionItems.length > 0) {
            setActiveIndex(0);
          }
        }
        break;
      case "End":
        if (isOpen) {
          event.preventDefault();
          setActiveIndex(optionItems.length - 1);
        }
        break;
      case "Enter":
      case " ":
        if (isOpen) {
          event.preventDefault();
          const active = optionItems[activeIndex];
          if (active) {
            handleSelect(active.option);
          }
        }
        break;
      case "Escape":
        if (isOpen) {
          event.preventDefault();
          closeDropdown();
        }
        break;
      default:
        break;
    }
  };

  return (
    <Container data-testid={dataTestId}>
      <Label htmlFor={fieldId} $hidden={hideLabel}>
        <LabelText>{label}</LabelText>
        {required ? <RequiredMark aria-hidden="true">*</RequiredMark> : null}
      </Label>

      <S.SelectRoot ref={containerRef} onBlur={handleContainerBlur}>
        <S.SelectTrigger
          id={fieldId}
          type="button"
          role="combobox"
          disabled={disabled}
          $readOnly={readOnly}
          onClick={handleToggle}
          onKeyDown={handleTriggerKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isExpanded}
          aria-controls={listboxId}
          aria-required={required || undefined}
          aria-disabled={readOnly || undefined}
          aria-activedescendant={
            isExpanded && activeIndex >= 0
              ? `${listboxId}-option-${activeIndex}`
              : undefined
          }
        >
          <S.SelectValue>{displayValue || " "}</S.SelectValue>
          <S.SelectIcon>
            <DropdownIcon />
          </S.SelectIcon>
        </S.SelectTrigger>

        {isExpanded ? (
          <S.Dropdown>
            <S.OptionList id={listboxId} role="listbox">
              {optionItems.length === 0 ? (
                <S.EmptyOption role="presentation">
                  Nenhuma opção disponível
                </S.EmptyOption>
              ) : (
                optionItems.map(({ option, key, label: optionLabel, selected }, index) => (
                  <S.OptionItem
                    key={key}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={selected}
                    $selected={selected}
                    $active={index === activeIndex}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option)}
                  >
                    {optionLabel}
                  </S.OptionItem>
                ))
              )}
            </S.OptionList>
          </S.Dropdown>
        ) : null}
      </S.SelectRoot>
    </Container>
  );
};

export default SelectField;
